import { ReceiptsApi } from 'services/api/apis/receipts/receipts.api';
import { Receipt } from 'services/api/models';

export interface PollReceiptHandlers {
  /** Called once when the receipt reaches COMPLETE. */
  onComplete: (receipt: Receipt) => void;
  /** Called once when the receipt reaches FAILED. */
  onFailed: (message: string) => void;
  /**
   * Called once if we stop polling before reaching a terminal state (too many
   * attempts, or repeated request failures). RSVPs now complete synchronously
   * server-side, so this is a safety net rather than the expected path.
   */
  onGaveUp: () => void;
}

const MAX_ATTEMPTS = 20;
const BASE_DELAY_MS = 250;
const MAX_DELAY_MS = 2000;

/**
 * Polls a ticket receipt until it reaches a terminal state, with bounded
 * attempts and exponential backoff. Replaces the previous unbounded 80ms poll
 * that could loop forever (and trip the API rate limit) whenever a receipt
 * stayed PENDING or the request kept failing.
 *
 * Returns a cancel function; call it on unmount / when the receipt id changes.
 */
export function pollReceiptUntilTerminal(
  getReceipt: ReceiptsApi['getReceipt'],
  receiptId: string,
  handlers: PollReceiptHandlers,
): () => void {
  let cancelled = false;
  let timerId: ReturnType<typeof setTimeout> | undefined;
  let attempts = 0;

  const poll = async (): Promise<void> => {
    if (cancelled) return;

    const result = await getReceipt(receiptId);

    if (cancelled) return;

    if (result.success) {
      const receipt = result.value;

      if (receipt.status === 'FAILED') {
        handlers.onFailed(
          receipt.errors?.length
            ? receipt.errors.join('\n')
            : 'Reservation failed. Please try again.',
        );
        return;
      }

      if (receipt.status === 'COMPLETE') {
        handlers.onComplete(receipt);
        return;
      }
    }

    attempts += 1;

    if (attempts >= MAX_ATTEMPTS) {
      handlers.onGaveUp();
      return;
    }

    const delay = Math.min(
      BASE_DELAY_MS * 2 ** Math.floor(attempts / 3),
      MAX_DELAY_MS,
    );

    timerId = setTimeout(() => {
      void poll();
    }, delay);
  };

  void poll();

  return () => {
    cancelled = true;
    if (timerId) clearTimeout(timerId);
  };
}
